// Filename: gani_acc.js
// Timestamp: 2018.12.09-00:20:37 (last modified)
// Author(s): bumblehead <chris@bumblehead.com>
//
// creates 'get' and 'set' methods for data
// on the given namespace

import {
  gnenumCHILD as CHILD,
  gnenumCHILDKEYS as CHILDKEYS,
  gnenumNSFKEY as NSFKEY,
  gnenumNSBASE as NSBASE,
  gnenumNSBASEELEM as NSBASEELEM,
  gnenumNSSUBJ as NSSUBJ,
  gnenumNSCACHE as NSCACHE,
  gnenumNSSUCH as NSSUCH,
  gnenumNSPART as NSPART,
  gnenumNSINIT as NSINIT,
  gnenumNSFULL as NSFULL,
  gnenumISPERSISTEDTS as ISPERSISTEDTS
} from './gnenum.js'

import {
  gnutilPlainObjIs as utilPlainObjIs,
  gnutilSpecObjIs as utilSpecObjIs
} from './gnutil.js'

// Specs list maintains a special format that allows gani to support
// and optimize some operations. 'static' values are defined at the head,
// if they exist, dynamic 'spec' values are defined at the tail.
const gnaccSpecAdd = (spec, adding) => {
  if (Array.isArray(spec)) {
    const spec0 = spec[0]
    if (utilSpecObjIs(spec0)) {
      spec = utilSpecObjIs(adding)
        ? spec.concat(adding)
        : [adding].concat(spec)
    } else if (utilPlainObjIs(spec0)) {
      spec = [Object.assign({}, spec0, adding)].concat(spec.slice(1))
    } else {
      spec = spec.concat(adding)
    }
  } else if (utilPlainObjIs(spec)) {
    spec = utilSpecObjIs(spec)
      ? [adding].concat(spec)
      : Object.assign({}, spec, adding)
  }

  return spec
}

const accessor = (ns, name = `${ns}hydrated`, acc = {}) => {
  acc.ns = ns

  acc.name = name

  acc.getns = () => ns

  acc.is = acc => acc.ns === ns

  acc.specpush = (node, def) => (
    acc.specset(node, gnaccSpecAdd(acc.specget(node), def)))

  acc.spechas = node => (
    node.has(ns))

  acc.specget = (node, def) => (
    node.get(ns, def))

  acc.specset = (node, def) =>(
    node.set(ns, def))

  acc.has = node => (
    node.has(name))

  acc.get = (node, def) => (
    node.get(name, def))

  acc.stamp = (node, date = Date.now()) => (
    node.set(ISPERSISTEDTS, date))

  acc.set = (node, def) => acc.stamp(
    node.set(name, def))

  acc.rm = node => acc.stamp(
    node.rm(name))

  acc.prophas = (node, prop) => (
    prop in acc.get(node, {}))

  acc.propget = (node, prop, def, val) => (
    val = acc.get(node, {})[prop],
    val === undefined ? def : val)

  acc.propset = (node, prop, val, nodedata) => (
    nodedata = acc.get(node, {}),
    nodedata = Array.isArray(nodedata)
      ? (nodedata[prop] = val, nodedata)
      : Object.assign({}, nodedata, { [prop]: val }),
    acc.set(node, nodedata))

  acc.assign = (node, obj, nodedata, existingonly) => {
    if (obj === null)
      return node

    nodedata = acc.get(node, Array.isArray(obj) ? [] : {})

    if (Array.isArray(nodedata))
      return acc.set(node, obj.slice())

    if (existingonly) {
      const objexistingonly = {}
      for (const key in (obj || {}))
        if (key in nodedata)
          objexistingonly[key] = obj[key]

      obj = objexistingonly
    }

    return acc.set(node, Object.assign({}, nodedata, (obj || {})))
  }

  acc.assignExisting = (node, obj, nodedata) => (
    acc.assign(node, obj, nodedata, true))

  return acc
}

// subj values are locally defined on the node after any child nodes
// are first defined
//
// they can be composed with values from fkey nodes.
//
// subj values have outgoing edges and updates to them are propogated
// to other nodes
//
// special subj values,
//   `classNameNode`, when defined, becomes the className 'name' of the node
//   `classNameRoot`, when defined, appears on root className of the node
const gnaccSubj = accessor(NSSUBJ)

// fkey values are defined with values that are keys to other nodes
//
// ex, fkey.user = '/data/users/user-5963'
//
// named property 'user' is a controlled static value and the key which it
// references may change.
//
const gnaccFKey = accessor(NSFKEY)

// cache values are only used by uipartial at this time. this is a mostly
// private namespace for direct usage by node scripts. use carefully.
//
const gnaccCache = accessor(NSCACHE)

// init values are locally defined on the node before any other data
// they can be composed with values from fkey nodes.
//
// init values never have outgoing edges and updates to them do not need
// to be propogated to other nodes
const gnaccInit = accessor(NSINIT)

// such, acronym for 'subject childs'. these rely on accumulators and  "upward"
// edge creators referencing data on childs. they are assembled and populated
// to accumulate values mid-hydration loop
const gnaccSuch = accessor(NSSUCH)

// full data used with node with subj value that is related to a list of
// other values, such as a list of user data for a user list
const gnaccFull = accessor(NSFULL)

// original un-modified model data sometimes give by parent node
// sometimes defined through local pattern
const gnaccBaseElem = accessor(NSBASEELEM)

// array result...
const gnaccPart = accessor(NSPART)

// 'base' is usually a list and was formerly named 'baselist'
// hydrated before childs, it is used to resolve data keys that are
// mapped to child patterns
const gnaccBase = accessor(NSBASE)

const gnaccChild = accessor(CHILD, CHILDKEYS)

const gnaccNSMap = {
  subj: gnaccSubj,
  fkey: gnaccFKey,
  init: gnaccInit,
  such: gnaccSuch,
  full: gnaccFull,
  part: gnaccPart,
  base: gnaccBase,
  baseelem: gnaccBaseElem,
  cache: gnaccCache,
  child: gnaccChild
}

export {
  gnaccNSMap as default,
  gnaccNSMap,
  gnaccSubj,
  gnaccFKey,
  gnaccCache,
  gnaccInit,
  gnaccSuch,
  gnaccFull,
  gnaccBaseElem,
  gnaccPart,
  gnaccBase,
  gnaccChild,
  gnaccSpecAdd
}
